import { useState } from "react";
import { App } from "antd";
import { redeemProduct, downloadComprobante } from "../api";
import { CLOTHING_CATEGORY, CHARGE_TYPE, PAYMENT_METHOD } from "../constants";

const initialSelection = {
  quantity: 1,
  chargeType: CHARGE_TYPE.INDIVIDUAL,
  paymentMethod: PAYMENT_METHOD.BRAVOS,
  size: null,
  color: null,
};

// Encapsula todo el flujo de canje: abrir modal, elegir opciones, validar y confirmar.
export default function usePurchase({ points, setPoints, setProducts }) {
  const { message } = App.useApp();
  const storedUserId = localStorage.getItem("user_id");
  const requesterId = storedUserId ? parseInt(storedUserId, 10) : null;

  const [product, setProduct] = useState(null);
  const [open, setOpen] = useState(false);
  const [selection, setSelection] = useState(initialSelection);
  const [selectedUsers, setSelectedUsers] = useState(requesterId ? [requesterId] : []);
  const [confirming, setConfirming] = useState(false);

  // Reinicia toda la selección: evita que talla/color/cantidad de un
  // producto anterior se queden pegados al abrir uno nuevo.
  const openModal = (p) => {
    setProduct(p);
    setSelection(initialSelection);
    setSelectedUsers(requesterId ? [requesterId] : []);
    setOpen(true);
  };

  const closeModal = () => setOpen(false);

  const updateSelection = (patch) => setSelection((prev) => ({ ...prev, ...patch }));

  const isClothing = product?.category === CLOTHING_CATEGORY;
  const unitCost = selection.paymentMethod === PAYMENT_METHOD.CASH ? product?.cash : product?.price;
  const totalCost = (unitCost || 0) * selection.quantity;
  const isCollective = selection.chargeType === CHARGE_TYPE.COLLECTIVE;
  const perUserCost =
    isCollective && selectedUsers.length > 0 ? Math.ceil(totalCost / selectedUsers.length) : null;

  const invalidQuantity = selection.quantity < 1 || selection.quantity > (product?.stock ?? 0);
  const missingClothingChoice = isClothing && (!selection.size || !selection.color);
  const insufficientBravos =
    !isCollective && selection.paymentMethod === PAYMENT_METHOD.BRAVOS && points < totalCost;

  const canConfirm = Boolean(product) && !invalidQuantity && !missingClothingChoice && !insufficientBravos;

  const confirm = async () => {
    if (!product) return;

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");
    if (!userId || !token) return message.error("Usuario no identificado");
    if (invalidQuantity) return message.warning("Cantidad inválida o sin stock suficiente");
    if (missingClothingChoice) return message.warning("Selecciona talla y color");
    if (insufficientBravos) return message.warning("Saldo insuficiente para esta cantidad.");

    setConfirming(true);
    try {
      const data = await redeemProduct({
        token,
        userId,
        product,
        quantity: selection.quantity,
        chargeType: selection.chargeType,
        paymentMethod: selection.paymentMethod,
        participants: selectedUsers,
        size: selection.size,
        color: selection.color,
      });

      const successMsg = isCollective
        ? selection.paymentMethod === PAYMENT_METHOD.CASH
          ? "Canje colectivo en efectivo iniciado. Los participantes deben completar su pago."
          : "Canje colectivo realizado correctamente"
        : `Canje exitoso${selection.paymentMethod === PAYMENT_METHOD.CASH ? " en efectivo" : ""}: ${selection.quantity} × ${product.name}`;

      message.success(successMsg);

      if (!isCollective && selection.paymentMethod === PAYMENT_METHOD.BRAVOS) {
        setPoints((prev) => prev - totalCost);
        setProducts((prev) =>
          prev.map((p) => (p.id === product.id ? { ...p, stock: p.stock - selection.quantity } : p))
        );
      }

      closeModal();

      if (data.comprobante) {
        await downloadComprobante(data.comprobante).catch((error) => {
          console.error("Error descargando comprobante:", error);
          message.error("No se pudo descargar el comprobante");
        });
      }
    } catch (error) {
      console.error(error);
      message.error(error.message || "Fallo en la comunicación con el servidor");
    } finally {
      setConfirming(false);
    }
  };

  return {
    product,
    open,
    openModal,
    closeModal,
    selection,
    updateSelection,
    selectedUsers,
    setSelectedUsers,
    requesterId,
    confirming,
    confirm,
    isClothing,
    isCollective,
    totalCost,
    perUserCost,
    canConfirm,
  };
}
